import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    FLOW_GROUPS,
    hasFlow,
    type FlowGroup,
    type LatestFlow,
} from '../../event/flow/groups';
import { formatNetCapital, formatWallets } from '../../event/flow/summary';
import { useLatestFlow } from '../../event/flow/useLatestFlow';
import type { CandidateEvent } from '../model/types';

export interface GroupFlow {
    group: FlowGroup;
    markets: number;
    single: LatestFlow | null;
}

export function groupFlows(
    byMarket: ReturnType<typeof useLatestFlow>['byMarket'],
    marketIds: readonly string[]
): GroupFlow[] {
    const result: GroupFlow[] = [];
    for (const group of FLOW_GROUPS) {
        const withFlow = marketIds
            .map(id => byMarket.get(id)?.get(group.id))
            .filter((f): f is LatestFlow => hasFlow(f));
        if (withFlow.length === 0) continue;
        result.push({
            group,
            markets: withFlow.length,
            single: marketIds.length === 1 ? withFlow[0] : null,
        });
    }
    return result;
}

function flowText(flow: GroupFlow): string {
    if (flow.single) return formatNetCapital(flow.single);
    return `${flow.markets} outcome${flow.markets === 1 ? '' : 's'}`;
}

function flowTitle(flow: GroupFlow): string {
    if (flow.single) {
        return `${flow.group.label}: ${formatNetCapital(flow.single)}, ${formatWallets(flow.single.wallets)}`;
    }
    return `${flow.group.label} flow in ${flow.markets} outcome${flow.markets === 1 ? '' : 's'}`;
}

export function FlowMark({ event }: { event: CandidateEvent }) {
    const { byMarket } = useLatestFlow(event.open ? event.id : null);
    const flows = groupFlows(
        byMarket,
        event.markets.map(m => m.marketId)
    );
    if (flows.length === 0) return <span />;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {flows.map(flow => (
                <Box
                    key={flow.group.id}
                    title={flowTitle(flow)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 0.75,
                        py: '1px',
                        borderRadius: '10px',
                        whiteSpace: 'nowrap',
                        bgcolor: alpha(flow.group.color, 0.12),
                    }}
                >
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            flexShrink: 0,
                            bgcolor: flow.group.color,
                        }}
                    />
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {flow.group.label} {flowText(flow)}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}
